import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';

/**
 * Performance Traffic Light — visual feedback for essay answers.
 * signal: "green" | "yellow" | "red" | null (pending)
 * size: "sm" | "md" | "lg"
 */
export const SIGNAL_META = {
  green: {
    label: 'Domínio Completo',
    short: 'Verde',
    description:
      'O Mestre reconheceu sua maestria: você citou os conceitos-chave e explicou a lógica corretamente.',
    quote: 'Seu monstro evoluiu em sabedoria! Continue assim, Treinador.',
    ring: 'ring-emerald-400',
    dot: 'bg-emerald-500',
    glow: 'shadow-[0_0_18px_rgba(16,185,129,0.55)]',
    text: 'text-emerald-700',
    border: 'border-emerald-400',
    soft: 'bg-emerald-50',
    gradient: 'from-emerald-400 via-emerald-500 to-teal-500',
    Icon: CheckCircle2,
  },
  yellow: {
    label: 'Desenvolvimento Parcial',
    short: 'Amarelo',
    description:
      'Caminho no rumo certo — a resposta é coerente, mas faltam elementos essenciais. Afiar detalhes te levará ao Verde.',
    quote: 'Seu monstro está quase lá — afina os detalhes e domine a próxima!',
    ring: 'ring-amber-400',
    dot: 'bg-amber-400',
    glow: 'shadow-[0_0_18px_rgba(251,191,36,0.6)]',
    text: 'text-amber-700',
    border: 'border-amber-400',
    soft: 'bg-amber-50',
    gradient: 'from-amber-300 via-yellow-400 to-orange-400',
    Icon: AlertTriangle,
  },
  red: {
    label: 'Necessidade de Reforço',
    short: 'Vermelho',
    description:
      'A resposta ainda está distante do gabarito. Revise os conceitos-chave e retorne — todo Mestre recomeça forte.',
    quote: 'O desafio te deixou mais forte — revise os conceitos e volte mais preparado!',
    ring: 'ring-rose-400',
    dot: 'bg-rose-500',
    glow: 'shadow-[0_0_18px_rgba(244,63,94,0.55)]',
    text: 'text-rose-700',
    border: 'border-rose-400',
    soft: 'bg-rose-50',
    gradient: 'from-rose-400 via-red-500 to-rose-600',
    Icon: XCircle,
  },
  pending: {
    label: 'Aguardando Avaliação',
    short: 'Pendente',
    description: 'O Mestre ainda está analisando sua resposta. Em breve o semáforo se acende!',
    quote: 'Paciência é sabedoria — o Mestre em breve revelará o resultado.',
    ring: 'ring-slate-300',
    dot: 'bg-slate-300',
    glow: '',
    text: 'text-slate-500',
    border: 'border-slate-300',
    soft: 'bg-slate-50',
    gradient: 'from-slate-300 to-slate-400',
    Icon: HelpCircle,
  },
};

const SIZE_MAP = {
  sm: { outer: 'w-7 h-7', inner: 'w-3 h-3' },
  md: { outer: 'w-10 h-10', inner: 'w-5 h-5' },
  lg: { outer: 'w-14 h-14', inner: 'w-7 h-7' },
};

export function PerformanceTrafficLight({ signal, size = 'md', showLabel = false }) {
  const meta = SIGNAL_META[signal] || SIGNAL_META.pending;
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  const animate = signal === 'green' || signal === 'yellow' || signal === 'red';

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid={`traffic-light-${signal || 'pending'}`}
            className={`inline-flex items-center gap-2 cursor-help select-none`}
          >
            <div
              className={`relative ${s.outer} rounded-full flex items-center justify-center bg-slate-900/90 ring-2 ${meta.ring} ${meta.glow} transition-transform hover:scale-110`}
            >
              <span
                className={`${s.inner} rounded-full ${meta.dot} ${
                  animate ? 'animate-pulse' : ''
                }`}
              />
              {animate && (
                <span
                  className={`absolute inset-0 rounded-full ${meta.dot} opacity-30 animate-ping`}
                  style={{ animationDuration: '2.2s' }}
                />
              )}
            </div>
            {showLabel && (
              <span className={`text-xs font-bold uppercase tracking-wide ${meta.text}`}>
                {meta.short}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs p-3 bg-slate-900 text-white border-slate-700"
        >
          <div className="flex items-center gap-2 mb-1">
            <meta.Icon className={`w-4 h-4 ${meta.text}`} />
            <span className="font-bold text-sm">{meta.label}</span>
          </div>
          <p className="text-xs text-slate-200 leading-relaxed">{meta.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default PerformanceTrafficLight;
